import {
  BlockStack,
  Box,
  Card,
  InlineGrid,
  InlineStack,
  Text,
} from '@shopify/polaris';
import type { NotificationItem } from '@/queries/notifications';

export interface HistoryHeroStatsProps {
  notifications: NotificationItem[];
}

export function HistoryHeroStats({ notifications }: HistoryHeroStatsProps) {
  const stats = computeStats(notifications);

  return (
    <InlineGrid columns={{ xs: 2, sm: 4 }} gap="300">
      <StatCard
        label="مبعوته (آخر 7 أيام)"
        value={stats.sentLast7Days.toLocaleString()}
        delta={stats.sentDelta}
      />
      <StatCard
        label="معدل الفتح"
        value={stats.openRate != null ? `${stats.openRate}%` : '—'}
        delta={null}
        tone={
          stats.openRate != null
            ? stats.openRate >= 20
              ? 'success'
              : stats.openRate >= 10
              ? 'info'
              : 'critical'
            : 'neutral'
        }
      />
      <StatCard
        label="معدل الضغط (CTR)"
        value={stats.ctr != null ? `${stats.ctr}%` : '—'}
        delta={null}
        tone={
          stats.ctr != null
            ? stats.ctr >= 5
              ? 'success'
              : stats.ctr >= 2
              ? 'info'
              : 'critical'
            : 'neutral'
        }
      />
      <StatCard
        label="Conversions"
        value={stats.conversions.toLocaleString()}
        delta={null}
      />
    </InlineGrid>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  delta: number | null;
  tone?: 'success' | 'info' | 'critical' | 'neutral';
}

function StatCard({ label, value, delta, tone = 'neutral' }: StatCardProps) {
  const toneColor =
    tone === 'success'
      ? 'success'
      : tone === 'critical'
      ? 'critical'
      : tone === 'info'
      ? 'magic'
      : undefined;

  return (
    <Card>
      <BlockStack gap="200">
        <Text as="p" variant="bodySm" tone="subdued">
          {label}
        </Text>
        <InlineStack gap="200" blockAlign="baseline" align="space-between">
          <Text as="span" variant="heading2xl" tone={toneColor}>
            {value}
          </Text>
          {delta != null && delta !== 0 ? (
            <Box>
              <Text
                as="span"
                variant="bodySm"
                tone={delta > 0 ? 'success' : 'critical'}
                fontWeight="medium"
              >
                {delta > 0 ? '↑' : '↓'} {Math.abs(delta)}%
              </Text>
            </Box>
          ) : null}
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

interface Stats {
  sentLast7Days: number;
  sentDelta: number | null;
  openRate: number | null;
  ctr: number | null;
  conversions: number;
}

function computeStats(notifications: NotificationItem[]): Stats {
  const now = Math.floor(Date.now() / 1000);
  const dayInSec = 86400;
  const sevenDaysAgo = now - 7 * dayInSec;
  const fourteenDaysAgo = now - 14 * dayInSec;

  let sentLast7 = 0;
  let sentPrev7 = 0;
  let totalOpened = 0;
  let totalClicked = 0;
  let totalSent = 0;
  let totalConv = 0;

  for (const n of notifications) {
    if (n.created_at >= sevenDaysAgo) sentLast7 += n.sent_count;
    else if (n.created_at >= fourteenDaysAgo) sentPrev7 += n.sent_count;
    totalSent += n.sent_count;
    totalOpened += n.opened_count;
    totalClicked += n.clicked_count;
    totalConv += n.converted_count;
  }

  const openRate = totalSent ? Math.round((totalOpened / totalSent) * 100) : null;
  const ctr = totalSent ? Math.round((totalClicked / totalSent) * 100) : null;

  let sentDelta: number | null = null;
  if (sentPrev7 > 0) {
    sentDelta = Math.round(((sentLast7 - sentPrev7) / sentPrev7) * 100);
  }

  return {
    sentLast7Days: sentLast7,
    sentDelta,
    openRate,
    ctr,
    conversions: totalConv,
  };
}
